import {
  AutopilotTaskDto,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useToggleTaskSteps,
} from '@urbanx/agx.activities.client'
import {
  AgxModal,
  AgxSpacer,
  AgxStepsIndicator,
  AgxTypography,
  useToast,
} from '@urbanx/agx.ui-components2'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ComplianceModalActionButtons from './ComplianceModalActionButtons'
import { CampaignDetailModel, DocumentGroup } from '@urbanx/agx.campaigns.client'
import TasksFilesList from 'pages/campaigns/components/campaignTasks/TasksFilesList'
import { BaseComplianceModalProps } from './BaseComplianceModalProps'

interface ComplianceModalProps {
  title: string
  tasks: AutopilotTaskDto[]
  open: boolean
  onClose: () => void
  documents: DocumentGroup[]
  campaignId: string
  campaign: CampaignDetailModel
  onSaveCurrentPage?: (task: AutopilotTaskDto) => void | Promise<void>
  renderPage: (props: BaseComplianceModalProps) => React.ReactNode
  completeDisabled?: boolean
}

const ComplianceModal = ({
  title,
  tasks,
  open,
  onClose,
  documents,
  campaignId,
  campaign,
  onSaveCurrentPage,
  renderPage,
  completeDisabled = false,
}: ComplianceModalProps) => {
  const [currentPage, setCurrentPage] = useState(0)
  const [localTasks, setLocalTasks] = useState<AutopilotTaskDto[]>(tasks)
  const persistedTasksRef = useRef<AutopilotTaskDto[]>(tasks)
  const wasOpenRef = useRef(open)

  useEffect(() => {
    if (!open) {
      setLocalTasks(tasks)
      persistedTasksRef.current = tasks
      setCurrentPage(0)
    }
  }, [tasks, open])

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setLocalTasks(tasks)
      persistedTasksRef.current = tasks
      setCurrentPage(0)
    }

    wasOpenRef.current = open
  }, [open, tasks])

  const currentTask = useMemo(() => localTasks[currentPage], [currentPage, localTasks])
  const areAllStepsCompleted = useMemo(
    () =>
      localTasks.every(
        (task) =>
          (task.taskSteps?.length ?? 0) > 0 &&
          (task.taskSteps ?? []).every((step) => step.isCompleted)
      ),
    [localTasks]
  )

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

  const { pushToast } = useToast()

  const { toggleTaskSteps } = useToggleTaskSteps({
    onSuccess: () => {
      invalidateTasks(campaignId)
    },
    onError: () => {
      pushToast({
        type: 'error',
        message: 'Failed to toggle task steps',
      })
    },
  })

  const toggleTaskStep = useCallback(
    (stepOrder: number, isChecked: boolean) => {
      setLocalTasks((prev) =>
        prev.map((task, index) => {
          if (index !== currentPage) {
            return task
          }

          return {
            ...task,
            taskSteps:
              task.taskSteps?.map((step) =>
                step.order === stepOrder
                  ? { ...step, isCompleted: isChecked }
                  : step
              ) ?? [],
          }
        })
      )
    },
    [currentPage]
  )

  const persistCurrentPage = useCallback(async () => {
    const persistedTask = persistedTasksRef.current[currentPage]
    const changedStepIds =
      currentTask?.taskSteps
        ?.filter((step) => {
          const persistedStep = persistedTask?.taskSteps?.find(
            (candidate) => candidate.order === step.order
          )

          return (persistedStep?.isCompleted ?? false) !== (step.isCompleted ?? false)
        })
        .map((step) => step.order ?? 0) ?? []

    if (onSaveCurrentPage && currentTask) {
      await onSaveCurrentPage(currentTask)
    }

    if (changedStepIds.length > 0 && currentTask?.category) {
      toggleTaskSteps({
        campaignId,
        category: currentTask.category,
        contractId: currentTask.contractId,
        stepIds: changedStepIds,
      })

      persistedTasksRef.current = persistedTasksRef.current.map((task, index) =>
        index === currentPage ? currentTask : task
      )
    }
  }, [campaignId, currentPage, currentTask, onSaveCurrentPage, toggleTaskSteps])

  const onPrevious = useCallback(async () => {
    await persistCurrentPage()
    setCurrentPage((prev) => Math.max(prev - 1, 0))
  }, [persistCurrentPage])

  const onNext = useCallback(async () => {
    await persistCurrentPage()
    setCurrentPage((prev) => Math.min(prev + 1, tasks.length - 1))
  }, [persistCurrentPage, tasks.length])

  const onSaveAndExit = useCallback(() => {
    persistCurrentPage()
    onClose()
  }, [onClose, persistCurrentPage])

  const onComplete = useCallback(() => {
    persistCurrentPage()
    onClose()
  }, [onClose, persistCurrentPage])

  return (
    <AgxModal
      type="fullScreen"
      variant={'withDivider'}
      actionButtonContainerStyle={'withDivider'}
      setIsDialogOpen={onClose}
      isOpen={open}
      secondaryTitle={
        <AgxSpacer direction="horizontal" contentAlignment="center" fill>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              width: '100%',
              gap: 'var(--gap-xl)',
            }}
          >
            <AgxTypography
              type="body_ui_strong"
              size="medium"
              id="titlemodal"
              color="var(--text-primary)"
            >
              {title}
            </AgxTypography>
            <AgxStepsIndicator
              totalLevels={tasks.length}
              currentLevel={currentPage + 1}
            />
          </div>
        </AgxSpacer>
      }
      content={
        <AgxSpacer
          direction="horizontal"
          padding={{
            top: 'md',
            bottom: 'md',
            left: 'xl',
            right: 'xl',
          }}
          fill
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '3fr 2fr',
              width: '100%',
              gap: 'var(--gap-xl)',
            }}
          >
            <AgxSpacer fill>
              <TasksFilesList files={documents} id={campaignId} />
            </AgxSpacer>
            <AgxSpacer direction="vertical" fill>
              {currentTask
                ? renderPage({
                    task: currentTask,
                    taskNumber: currentPage + 1,
                    campaign,
                    toggleTaskStep,
                  })
                : null}
            </AgxSpacer>
          </div>
        </AgxSpacer>
      }
      actionButtons={
        <ComplianceModalActionButtons
          currentPage={currentPage}
          totalPages={tasks.length}
          onPrevious={onPrevious}
          onNext={onNext}
          onComplete={onComplete}
          onSaveAndExit={onSaveAndExit}
          completeDisabled={completeDisabled || !areAllStepsCompleted}
        />
      }
    />
  )
}

export default ComplianceModal
